import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Save,
  Send,
  ArrowLeft,
  Image,
  X,
  Plus,
} from 'lucide-react';
import { createNews, updateNews, SECTORS_21 } from '../../utils/api/newsApi';
import { useLanguage } from '../../context/LanguageContext';
import { getImageUrl, uploadImageFile } from '../../utils/imageUtils';

const content = {
  ne: {
    pageTitle: 'लेख सिर्जना गर्नुहोस्',
    pageTitleEdit: 'लेख सम्पादन गर्नुहोस्',
    title: 'लेख शीर्षक',
    titlePlaceholder: 'लेखको शीर्षक लेख्नुहोस्...',
    summary: 'संक्षिप्त सारांश',
    summaryPlaceholder: 'लेखको संक्षिप्त विवरण...',
    content: 'पूर्ण सामग्री',
    contentPlaceholder: 'तपाईंको लेखको पूर्ण सामग्री यहाँ लेख्नुहोस्...',
    category: 'कोटि',
    selectCategory: 'कोटि चयन गर्नुहोस्',
    sector: 'सम्बन्धित क्षेत्र',
    selectSector: 'क्षेत्र चयन गर्नुहोस्',
    relatedEvent: 'सम्बन्धित कार्यक्रम (वैकल्पिक)',
    selectEvent: 'कार्यक्रम चयन गर्नुहोस्',
    featuredImage: 'मुख्य तस्वीर',
    uploadImage: 'तस्वीर अपलोड गर्नुहोस्',
    dragDrop: 'वा तस्वीर यहाँ छोड्नुहोस्',
    tags: 'ट्यागहरू',
    tagsPlaceholder: 'ट्याग थप्नुहोस् र Enter थिच्नुहोस्',
    saveDraft: 'मस्यौदाको रूपमा सुरक्षित गर्नुहोस्',
    submitReview: 'समीक्षाको लागि पेश गर्नुहोस्',
    successDraft: 'लेख मस्यौदाको रूपमा सुरक्षित गरियो!',
    successSubmit: 'लेख समीक्षाको लागि पेश गरियो!',
    back: 'पछाडि जानुहोस्',
    failedDraft: 'Failed to save draft.',
    failedSubmit: 'Failed to submit article.',
    failedUpload: 'Failed to upload image.',
    submitting: 'Submitting…',
    savingDraft: 'Saving…',
    uploadingImage: 'Uploading image…',
  },
  en: {
    pageTitle: 'Create Article',
    pageTitleEdit: 'Edit Article',
    title: 'Article Title',
    titlePlaceholder: 'Enter article title...',
    summary: 'Short Summary',
    summaryPlaceholder: 'Brief description of the article...',
    content: 'Full Content',
    contentPlaceholder: 'Write your full article content here...',
    category: 'Category',
    selectCategory: 'Select category',
    sector: 'Related Sector',
    selectSector: 'Select sector',
    relatedEvent: 'Related Event (optional)',
    selectEvent: 'Select event',
    featuredImage: 'Featured Image',
    uploadImage: 'Upload Image',
    dragDrop: 'or drag and drop',
    tags: 'Tags',
    tagsPlaceholder: 'Add tags and press Enter',
    saveDraft: 'Save as Draft',
    submitReview: 'Submit for Review',
    successDraft: 'Article saved as draft!',
    successSubmit: 'Article submitted for review!',
    back: 'Go Back',
    failedDraft: 'Failed to save draft.',
    failedSubmit: 'Failed to submit article.',
    failedUpload: 'Failed to upload image.',
    submitting: 'Submitting…',
    savingDraft: 'Saving…',
    uploadingImage: 'Uploading image…',
  },
};

const categories = ['Events', 'Development', 'Public Participation', 'Education', 'Politics', 'Infrastructure', 'Culture'];
const eventOptions = ['Vision 2050 Launch Program', 'Ward Level Citizen Consultation', 'Green Itahari Plantation Drive', 'Infrastructure Development Review'];

const CreateArticle = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const t = content[language] || content.en;

  const editArticle = location.state?.article;
  const viewOnly = location.state?.viewOnly;
  const isEditing = !!editArticle && !viewOnly;

  const [form, setForm] = useState({
    title: '',
    summary: '',
    content: '',
    category: '',
    sector: '',
    relatedEvent: '',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');

  // imagePreview: what we show in the <img> (local blob/base64 while picking, or existing server URL)
  // imageFile: the raw File object, only set when the user picks a NEW image that still needs uploading
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editArticle) {
      setForm({
        title: editArticle.title || '',
        summary: editArticle.excerpt || editArticle.summary || '',
        content: editArticle.content || '',
        category: editArticle.category || '',
        sector: editArticle.sector || '',
        relatedEvent: editArticle.relatedEvent || '',
        tags: editArticle.tags || [],
      });
      const img = editArticle.imageUrl || editArticle.coverImage || editArticle.image;
      if (img) {
        setImagePreview(img); // keep the original server value; getImageUrl() resolves it for display
      }
    }
  }, [editArticle]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!form.tags.includes(tagInput.trim())) {
        handleChange('tags', [...form.tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    handleChange('tags', form.tags.filter((t) => t !== tag));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    // Local preview only — the actual upload happens on save/submit.
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
  };

  /**
   * Resolves the image to send to the backend:
   * - If the user picked a new file, upload it and use the returned server URL.
   * - Else if editing and an image already existed, keep it.
   * - Else there's no image.
   */
  const resolveImageForSave = async () => {
    if (imageFile) {
      return await uploadImageFile(imageFile);
    }
    if (isEditing) {
      return editArticle.imageUrl || editArticle.coverImage || editArticle.image || '';
    }
    return '';
  };

  const buildPayload = (finalImageUrl) => ({
    title: form.title,
    excerpt: form.summary,
    content: form.content,
    sector: form.sector || form.category,
    category: form.category,
    imageUrl: finalImageUrl,
    coverImage: finalImageUrl,
    status: 'pending',
  });

  const handleSaveDraft = async () => {
    setSubmitting(true);
    setError('');
    try {
      const finalImageUrl = await resolveImageForSave();
      const payload = buildPayload(finalImageUrl);
      if (isEditing && editArticle?._id) {
        await updateNews(editArticle._id, payload);
      } else {
        await createNews(payload);
      }
      setSuccessMessage(t.successDraft);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err?.message || t.failedDraft);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReview = async () => {
    setSubmitting(true);
    setError('');
    try {
      const finalImageUrl = await resolveImageForSave();
      const payload = buildPayload(finalImageUrl);
      if (isEditing && editArticle?._id) {
        await updateNews(editArticle._id, payload);
      } else {
        await createNews(payload);
      }
      setSuccessMessage(t.successSubmit);
      setTimeout(() => {
        setSuccessMessage('');
        navigate('/reporter/articles');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || err?.message || t.failedSubmit);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/reporter/dashboard')}
        className="flex items-center gap-2 text-sm font-semibold text-char-500 transition-colors hover:text-primary"
      >
        <ArrowLeft size={16} />
        {t.back}
      </button>

      <h2 className="text-2xl font-extrabold text-char-900">
        {viewOnly ? t.pageTitle : isEditing ? t.pageTitleEdit : t.pageTitle}
      </h2>

      {/* Success message */}
      {successMessage && (
        <div className="rounded-2xl bg-primary-100 px-4 py-3 text-sm font-bold text-primary">
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600 border border-red-200">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main form */}
        <div className="space-y-5 lg:col-span-2">
          <div className="rounded-3xl border border-char-100 bg-white p-5 shadow-card sm:p-6">
            {/* Title */}
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.title}</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={t.titlePlaceholder}
                readOnly={viewOnly}
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-3 text-sm font-semibold text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 read-only:opacity-70"
              />
            </div>

            {/* Summary */}
            <div className="mt-5 space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.summary}</label>
              <textarea
                value={form.summary}
                onChange={(e) => handleChange('summary', e.target.value)}
                placeholder={t.summaryPlaceholder}
                readOnly={viewOnly}
                rows={2}
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-3 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 read-only:opacity-70 resize-none"
              />
            </div>

            {/* Content */}
            <div className="mt-5 space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.content}</label>
              <textarea
                value={form.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder={t.contentPlaceholder}
                readOnly={viewOnly}
                rows={10}
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-3 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 read-only:opacity-70 resize-y min-h-[200px]"
              />
            </div>
          </div>

          {/* Tags */}
          <div className="rounded-3xl border border-char-100 bg-white p-5 shadow-card sm:p-6">
            <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.tags}</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {form.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary"
                >
                  #{tag}
                  {!viewOnly && (
                    <button onClick={() => handleRemoveTag(tag)} className="hover:text-error transition-colors">
                      <X size={12} />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {!viewOnly && (
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder={t.tagsPlaceholder}
                className="mt-3 w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
              />
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Category & Sector */}
          <div className="rounded-3xl border border-char-100 bg-white p-5 shadow-card sm:p-6">
            <div className="space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.category}</label>
              <select
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                disabled={viewOnly}
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 disabled:opacity-70"
              >
                <option value="">{t.selectCategory}</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="mt-4 space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.sector}</label>
              <select
                value={form.sector}
                onChange={(e) => handleChange('sector', e.target.value)}
                disabled={viewOnly}
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 disabled:opacity-70"
              >
                <option value="">{t.selectSector}</option>
                {SECTORS_21.map((sec) => (
                  <option key={sec} value={sec}>{sec}</option>
                ))}
              </select>
            </div>

            <div className="mt-4 space-y-1.5">
              <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.relatedEvent}</label>
              <select
                value={form.relatedEvent}
                onChange={(e) => handleChange('relatedEvent', e.target.value)}
                disabled={viewOnly}
                className="w-full rounded-xl border border-char-100 bg-char-50 px-4 py-2.5 text-sm text-char-900 outline-none transition-all focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100 disabled:opacity-70"
              >
                <option value="">{t.selectEvent}</option>
                {eventOptions.map((ev) => (
                  <option key={ev} value={ev}>{ev}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Featured Image */}
          <div className="rounded-3xl border border-char-100 bg-white p-5 shadow-card sm:p-6">
            <label className="text-xs font-extrabold uppercase tracking-wider text-char-500">{t.featuredImage}</label>
            {imagePreview ? (
              <div className="mt-3 relative">
                <img src={getImageUrl(imagePreview)} alt="Preview" className="w-full rounded-2xl object-cover max-h-40" />
                {!viewOnly && (
                  <button
                    onClick={handleRemoveImage}
                    className="absolute right-2 top-2 rounded-full bg-white/90 p-1 shadow-sm hover:bg-error-50 hover:text-error transition-colors"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ) : (
              <label className={`mt-3 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-char-200 bg-char-50 px-4 py-6 transition-all hover:border-primary-300 hover:bg-primary-50 ${viewOnly ? 'cursor-not-allowed opacity-60' : ''}`}>
                <Image size={24} className="text-char-300" />
                <span className="mt-2 text-xs font-semibold text-char-500">{t.uploadImage}</span>
                <span className="text-xs text-char-400">{t.dragDrop}</span>
                {!viewOnly && (
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                )}
              </label>
            )}
          </div>

          {/* Action buttons */}
          {!viewOnly && (
            <div className="space-y-3">
              <button
                onClick={handleSubmitReview}
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-extrabold text-white shadow-green transition-all hover:-translate-y-0.5 hover:bg-primary-500 hover:shadow-green-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={16} />
                {submitting ? t.submitting : t.submitReview}
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-char-200 bg-white py-3 text-sm font-extrabold text-char-600 transition-all hover:border-char-300 hover:bg-char-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={16} />
                {submitting ? t.savingDraft : t.saveDraft}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateArticle;